// E2EE verification UX (F-SEC-1): unmissable badge with three live states,
// click → plain-language security sheet including the model in use and the
// §5.7 threat-model summary. Honesty is the product: no overclaiming.

import { memo, useCallback, useState } from 'react';

import { probeRender } from '../perf/renderProbe';
import { useCallStore } from '../store/callStore';

const LABELS = {
  on: 'End-to-end encrypted',
  degraded: 'Encryption degraded',
  'wrong-key': 'Wrong meeting key',
  off: 'Not end-to-end encrypted',
  unsupported: 'Encryption unsupported',
} as const;

export const EncryptionBadge = memo(function EncryptionBadge() {
  probeRender('EncryptionBadge');
  const state = useCallStore((s) => s.e2eeState);
  const [sheetOpen, setSheetOpen] = useState(false);
  const toggleSheet = useCallback(() => setSheetOpen((v) => !v), []);

  return (
    <>
      <button
        className={`bz-pill bz-e2ee-badge bz-e2ee-${state}`}
        onClick={toggleSheet}
        title="Meeting security details"
      >
        <span className="bz-e2ee-dot" /> {LABELS[state]}
      </button>
      {sheetOpen && <SecuritySheet state={state} onClose={toggleSheet} />}
    </>
  );
});

function SecuritySheet({ state, onClose }: { state: keyof typeof LABELS; onClose: () => void }) {
  return (
    <div className="bz-sheet-backdrop" onClick={onClose}>
      <div
        className="bz-sheet"
        role="dialog"
        aria-label="Meeting security"
        onClick={stopPropagation}
      >
        <h2>Meeting security</h2>
        {state === 'on' && (
          <>
            <p>
              <strong>This meeting is end-to-end encrypted.</strong> Audio, video, screen shares,
              and chat are encrypted on each attendee&rsquo;s device and decrypted only on other
              attendees&rsquo; devices. The servers relaying this call cannot read any of it.
            </p>
            <p>
              Key model: a per-session key generated by the Brizina backend and handed only to
              admitted attendees, then deleted when the meeting ends. This protects against the
              media infrastructure and networks in between; it means trusting the Brizina backend
              for the duration of the call.
            </p>
          </>
        )}
        {state === 'degraded' && (
          <p>
            <strong>Encryption is enabled but currently degraded</strong> — at least one
            attendee&rsquo;s media can&rsquo;t be verified as encrypted, or decryption errors are
            occurring. Media from healthy attendees remains encrypted.
          </p>
        )}
        {state === 'wrong-key' && (
          <p>
            <strong>The passphrase doesn&rsquo;t match the other attendees.</strong> This is a
            self-managed (zero-knowledge) meeting, so nobody can recover it for you — leave and
            rejoin with the exact passphrase the organizer shared. Your own media is still
            encrypted; you just can&rsquo;t read theirs, and they can&rsquo;t read yours.
          </p>
        )}
        {state === 'off' && (
          <p>
            <strong>This meeting is not end-to-end encrypted.</strong> Media is protected in
            transit (TLS/SRTP) but the meeting infrastructure could technically access it. Meetings
            created from this version onward are end-to-end encrypted by default.
          </p>
        )}
        <h3>What encryption does not cover</h3>
        <p>
          A compromised attendee device; another attendee sharing what they see (they hold the key
          by definition); and metadata — who met whom, when, and for how long is visible to the
          service to route the call.
        </p>
        <button className="bz-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

function stopPropagation(e: { stopPropagation: () => void }) {
  e.stopPropagation();
}
